/**
 * The domain, as one file, because the whole product is one idea: a human and an agent are the
 * same kind of thing.
 *
 * Names here are canonical — `docs/DATA_MODEL_AND_API.md` fixes them and nothing may rename an
 * entity. The rule that matters more than any individual type is the one below:
 *
 *   THERE IS NO `Human` TYPE AND NO `Agent` TYPE.
 *
 * There is `Identity`, and it carries `kind`. Every attempt to build this kind of system starts by
 * modelling people and then "adds agents later", and what arrives later is a second table, a second
 * review engine, a second policy tree and a second set of bugs. `AgentProfile` exists as a 1:1
 * extension precisely so that the temptation to make a second identity table has nowhere to go:
 * the extra facts an agent carries are in the extension, and the identity itself is shared.
 */

// ── the two kinds ──────────────────────────────────────────────────────────────────────────────

/** What can act. There will never be a third value without a migration and an argument. */
export type IdentityKind = "human" | "agent";

/**
 * Status vocabulary, fixed by the design system. `ok` verified/allowed, `warn` needs review,
 * `bad` blocked/critical, `dim` neutral. The UI maps these to `--ok` / `--warn` / `--bad` /
 * `--muted` and nothing else may invent a fifth.
 */
export type StatusTone = "ok" | "warn" | "bad" | "dim";

export type IdentityStatus = "active" | "suspended" | "retired";

/**
 * One lifecycle, two vocabularies.
 *
 * A person joins, moves and leaves; an agent is registered, rescoped and retired. These are the
 * same four states — created, running, changed, gone — and the union is kept rather than collapsed
 * because the words are what a reviewer reads on the screen. What is NOT permitted is a separate
 * lifecycle engine per kind: {@link isTerminal} and the transition rules are shared.
 */
export type LifecycleState =
  | "joiner" | "active" | "mover" | "leaver"
  | "new" | "rescoped" | "retired";

/** The states from which nothing further is expected, whichever kind reached them. */
export function isTerminal(state: LifecycleState): boolean {
  return state === "leaver" || state === "retired";
}

/** The lifecycle words that belong to each kind, for validation and for copy. */
export const LIFECYCLE_BY_KIND: Record<IdentityKind, readonly LifecycleState[]> = {
  human: ["joiner", "active", "mover", "leaver"],
  agent: ["new", "active", "rescoped", "retired"],
} as const;

export interface Identity {
  id: string;
  kind: IdentityKind;
  displayName: string;
  status: IdentityStatus;
  lifecycleState: LifecycleState;
  sourceSystem: string;
  externalId: string | null;
  /**
   * Required when `kind === "agent"`, and enforced by a database CHECK rather than only here.
   *
   * The Agents screen has a tab called "No owner" whose count must read 0. A constraint is the only
   * thing that makes that claim worth reading: a validation in application code is a claim about
   * the code paths somebody remembered, and the row that breaks it always arrives through the one
   * they did not.
   *
   * Note what is NOT constrained: a human need not have an owner. The builder's generated PRD
   * proposed `CHECK (ownerId IS NOT NULL)` on every identity, which would make the estate's root
   * administrators unrepresentable. Parity means the same lifecycle and the same review — not
   * pretending a person is owned by another person.
   */
  ownerIdentityId: string | null;
  createdAt: string;
  lastSeenAt: string | null;
}

/** 1:1 with an `Identity` whose kind is `agent`. Never with a human. */
export interface AgentProfile {
  identityId: string;
  purpose: string;
  capabilityTier: CapabilityTier;
  registeredTools: string[];
  /** `DataClass` ids. The set an agent may reach — the basis of `agent_scope_match`. */
  dataScopes: string[];
  owningTeam: string;
}

// ── capability tiers ───────────────────────────────────────────────────────────────────────────

export type CapabilityTier = 1 | 2 | 3;

/**
 * What a tier means, and what it costs its holder. Governance fixes these numbers; they are here
 * rather than in a screen because the TTL is enforced at issuance and the approval depth is
 * enforced at routing, and both must read the same table as the copy a reviewer sees.
 */
export interface TierRule {
  tier: CapabilityTier;
  meaning: string;
  /** Maximum credential lifetime, in seconds. Issuance clamps to this; it is not advisory. */
  maxTtlSeconds: number;
  /** Does a decision need an approver from outside the owning team, holding a veto? */
  requiresIndependentApproval: boolean;
  /** Must a human confirm each write at the moment it happens? */
  requiresHumanStepUpPerWrite: boolean;
  controls: string;
}

export const TIER_RULES: Record<CapabilityTier, TierRule> = {
  1: {
    tier: 1,
    meaning: "read-only",
    maxTtlSeconds: 30 * 60,
    requiresIndependentApproval: false,
    requiresHumanStepUpPerWrite: false,
    controls: "Owner approval; 30-minute TTL; standard monitoring",
  },
  2: {
    tier: 2,
    meaning: "reversible actions",
    maxTtlSeconds: 15 * 60,
    requiresIndependentApproval: false,
    requiresHumanStepUpPerWrite: false,
    controls: "Owner approval; 15-minute TTL; volume caps; anomaly escalation",
  },
  3: {
    tier: 3,
    meaning: "irreversible or financial",
    maxTtlSeconds: 5 * 60,
    requiresIndependentApproval: true,
    requiresHumanStepUpPerWrite: true,
    controls: "Owner + independent security; 5-minute TTL; human step-up per write; adversarial test before promotion",
  },
};

/**
 * Higher tier, SHORTER life. This reads backwards the first time and is the whole design: the more
 * damage a credential could do, the less time it exists for. Asserted here so that a future edit
 * which "rounds the numbers up" fails a test rather than a review.
 */
export function ttlCeilingFor(tier: CapabilityTier): number {
  return TIER_RULES[tier].maxTtlSeconds;
}

// ── data and systems ───────────────────────────────────────────────────────────────────────────

export type DataTier = "public" | "internal" | "confidential" | "sensitive" | "financial";

export interface DataClass {
  id: string;
  name: string;
  tier: DataTier;
  region: string;
}

export interface SystemRecord {
  id: string;
  name: string;
  kind: "internal" | "saas" | "cloud";
  onboardedAt: string;
  ownerIdentityId: string | null;
}

/**
 * The two tiers that make a standing grant a risk path rather than merely a grant.
 *
 * `EntitlementEdge.isStanding && object.tier in (sensitive, financial)` is the definition in
 * `docs/DATA_MODEL_AND_API.md`, and it is expressed once, here, so the graph query and the
 * dashboard's "highest-risk paths" cannot drift into disagreeing about what they are counting.
 */
export const RISK_DATA_TIERS: readonly DataTier[] = ["sensitive", "financial"] as const;

export function isSensitiveTier(tier: DataTier): boolean {
  return RISK_DATA_TIERS.includes(tier);
}

// ── credentials and elevation ──────────────────────────────────────────────────────────────────

export type CredentialKind = "jit_token" | "standing_key" | "workload_identity";

export interface Credential {
  id: string;
  identityId: string;
  kind: CredentialKind;
  scope: CredentialScope;
  /** Ticket or task id. Nullable for humans; an agent JIT token without one is refused. */
  purposeRef: string | null;
  issuedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  /** Whatever the PAM vendor calls this token on its side. Opaque here on purpose. */
  providerRef: string | null;
}

export interface CredentialScope {
  systems: string[];
  dataClasses: string[];
  actions: Action[];
}

/**
 * `jit_token` is the only kind that is short-lived by construction. The other two are exceptions.
 *
 * Accepts `Date` as well as the domain's ISO string, because the same question is asked by the API
 * (which holds `Date`s from the database) and by anything reading JSON. Requiring a conversion at
 * each call site is what produces the casts that suppress type errors — and one of those casts hid
 * a real mismatch here until `tsc --noEmit` was added to the test runner.
 */
export function isShortLived(c: { kind: CredentialKind; expiresAt: string | Date | null }): boolean {
  return c.kind === "jit_token" && c.expiresAt !== null;
}

export interface Elevation {
  id: string;
  identityId: string;
  grant: string;
  ttlSeconds: number;
  purposeRef: string | null;
  startsAt: string;
  endsAt: string;
  endedReason: "ttl" | "ticket_closed" | "revoked" | null;
}

// ── requests, decisions, policy ────────────────────────────────────────────────────────────────

export type Action = "read" | "write" | "delete" | "execute" | "approve" | "export";

/**
 * The actions a tier-3 rule cares about: those that cannot be undone by repeating the opposite.
 * `execute` is here because an execution's effects are not knowable from its name.
 */
export const IRREVERSIBLE_ACTIONS: readonly Action[] = ["write", "delete", "execute", "export"] as const;

export type RequestStatus = "pending" | "approved" | "denied" | "expired";

export interface AccessRequest {
  id: string;
  requesterIdentityId: string;
  /** Who the access is FOR. A human may request on behalf of an agent they own. */
  subjectIdentityId: string;
  action: Action;
  resourceRef: string;
  dataClassId: string | null;
  justification: string;
  purposeRef: string | null;
  status: RequestStatus;
  approverIdentityId: string | null;
  expiresAt: string | null;
  /**
   * Set when an approval was granted for scope the subject's tier does not cover. It is not a
   * warning to a human reader — the review engine selects on it, and the credential it produced
   * carries a 30-day expiry instead of the tier's ceiling.
   */
  reviewFlag: boolean;
  decidedAt: string | null;
}

export type PolicyVerdict = "allow" | "deny" | "step_up";

export interface PolicyDecision {
  id: string;
  requestId: string | null;
  subjectIdentityId: string;
  action: Action;
  resourceRef: string;
  decision: PolicyVerdict;
  policy: string;
  policyVersion: number;
  reason: string;
  /** SHA-256 of the canonical input. The decision is reproducible or it is not evidence. */
  inputHash: string;
  decidedAt: string;
}

export interface Policy {
  id: string;
  name: string;
  description: string;
  rego: string;
  version: number;
  enforced: boolean;
  updatedBy: string;
  updatedAt: string;
}

/** The exact shape OPA is handed. Fixed by `docs/DATA_MODEL_AND_API.md` → policy input contract. */
export interface PolicyInput {
  subject: {
    id: string;
    kind: IdentityKind;
    tier: CapabilityTier;
    owningTeam: string;
    credentialTtlRemaining: number;
    /** The agent's declared data scopes. Empty for a human, and for a brand-new agent. */
    dataScopes: string[];
  };
  action: Action;
  resource: {
    system: string;
    dataClass: string;
    tier: DataTier;
    region: string;
    owner: string;
  };
  context: {
    timeUtc: string;
    networkZone: string;
    anomalyScore: number;
    taskRef: string;
  };
  purpose: {
    declared: string;
    activeTicket: boolean;
  };
}

export interface PolicyResult {
  result: PolicyVerdict;
  policy: string;
  version: number;
  reason: string;
}

// ── entitlements ───────────────────────────────────────────────────────────────────────────────

export interface EntitlementBundle {
  id: string;
  name: string;
  forKind: IdentityKind;
  grants: string[];
  memberCount: number;
  sensitiveDataClassIds: string[];
  lastCertifiedAt: string | null;
}

export interface EntitlementEdge {
  id: string;
  subjectId: string;
  objectId: string;
  permission: Action;
  source: string;
  isStanding: boolean;
  lastVerifiedAt: string | null;
}

// ── runtime and review ─────────────────────────────────────────────────────────────────────────

export type AnomalySignal =
  | "volume" | "timing" | "new_tool_combo" | "scope_probe" | "goal_drift";

export interface AnomalyEvent {
  id: string;
  identityId: string;
  actionRef: string;
  signal: AnomalySignal;
  severity: Exclude<StatusTone, "dim">;
  escalationStatus: "open" | "reviewing" | "resolved";
  resolution: string | null;
  createdAt: string;
}

export type ReviewScope =
  | "human_standard" | "human_privileged" | "agent" | "third_party" | "data_level";

export interface ReviewCycle {
  id: string;
  scope: ReviewScope;
  cadence: "monthly" | "quarterly" | "contract_milestone";
  dueAt: string;
  status: "scheduled" | "in_progress" | "exceptions_pending" | "closed";
  progressPct: number;
  evidenceLockedAt: string | null;
}

/**
 * Agent scope is monthly where standard human access is quarterly.
 *
 * Not a tightening for its own sake: non-human identities are created and rescoped by automation,
 * so the population drifts between two quarterly reviews in a way a headcount does not.
 */
export const REVIEW_CADENCE: Record<ReviewScope, ReviewCycle["cadence"]> = {
  human_standard: "quarterly",
  human_privileged: "monthly",
  agent: "monthly",
  third_party: "contract_milestone",
  data_level: "quarterly",
};

export interface ReviewItem {
  id: string;
  cycleId: string;
  entitlementEdgeId: string;
  reviewerIdentityId: string;
  decision: "keep" | "remove" | null;
  justification: string | null;
  decidedAt: string | null;
}

// ── governance ─────────────────────────────────────────────────────────────────────────────────

export type RiskTier = "low" | "moderate" | "high";
export type ChecklistStatus = "not_started" | "in_progress" | "complete" | "waived";

export interface ChecklistItem {
  id: string;
  label: string;
  mandatory: boolean;
  status: ChecklistStatus;
  /** Required when status is `waived`. Enforced in `validateChecklist`, not by convention. */
  waiverReason: string | null;
}

export interface OnboardingIntake {
  id: string;
  kind: "agent" | "system" | "third_party";
  name: string;
  ownerIdentityId: string | null;
  purpose: string;
  requestedTier: CapabilityTier | null;
  scorecard: Scorecard;
  weightedScore: number;
  riskTier: RiskTier;
  checklist: ChecklistItem[];
  status: "draft" | "submitted" | "approved" | "rejected";
  submittedAt: string | null;
  decidedBy: string | null;
  decision: "approved" | "rejected" | null;
}

/** The six dimensions, scored 0–5 by Security. Never self-graded — that is a process rule the UI states. */
export interface Scorecard {
  identityAndAccess: number;
  dataProtection: number;
  networkExposure: number;
  appsecAndSupplyChain: number;
  monitoringAndResponse: number;
  complianceAndGovernance: number;
}

export interface CapabilitySafeguardReport {
  id: string;
  agentIdentityId: string;
  version: number;
  capabilities: string[];
  limitations: string[];
  safeguards: string[];
  approvedBy: string | null;
  approvedAt: string | null;
}

// ── audit ──────────────────────────────────────────────────────────────────────────────────────

export interface AuditEvent {
  id: string;
  /** Monotonic, from a database sequence. Verification walks this ascending. */
  seq: number;
  occurredAt: string;
  actorIdentityId: string | null;
  action: string;
  subjectType: string;
  subjectId: string;
  payloadHash: string;
  prevHash: string;
  hash: string;
}
